/**
 * LumpedNode - Aggregated tier node component
 *
 * Renders a lumped node that aggregates multiple accounts from Tier B or C.
 * Shows total balance and expand indicator.
 *
 * WP-FLOW-6: Updated to use light background + border styling
 * - Shows "(mixed)" label when containing both assets and liabilities
 * - Shows "(liabilities)" label when containing only liabilities
 */

import { memo, useState, useCallback } from 'react';
import type { LumpedNode as LumpedNodeType, NodeProps } from '../types';
import { isLiabilityAccount } from '../types';
import { NODE, TIER_CONFIG, A11Y } from '../constants';
import { formatCurrency } from '@/utils/format';

export interface LumpedNodeProps extends NodeProps<LumpedNodeType> {
  /** Called when mouse enters the node */
  onMouseEnter?: (node: LumpedNodeType, event: React.MouseEvent) => void;
  /** Called when mouse leaves the node */
  onMouseLeave?: (node: LumpedNodeType) => void;
  /** Whether this node is currently highlighted */
  isHighlighted?: boolean;
  /** Whether this node is currently expanded */
  isExpanded?: boolean;
}

function LumpedNodeComponent({
  node,
  x,
  y,
  width,
  height,
  onClick,
  onMouseEnter,
  onMouseLeave,
  isHighlighted = false,
  isExpanded = false,
}: LumpedNodeProps) {
  const [isHovered, setIsHovered] = useState(false);
  const [isFocused, setIsFocused] = useState(false);

  const handleClick = useCallback(() => {
    onClick?.(node);
  }, [onClick, node]);

  const handleKeyDown = useCallback(
    (e: React.KeyboardEvent) => {
      if (e.key === 'Enter' || e.key === ' ') {
        e.preventDefault();
        onClick?.(node);
      }
    },
    [onClick, node]
  );

  const handleMouseEnter = useCallback(
    (e: React.MouseEvent) => {
      setIsHovered(true);
      onMouseEnter?.(node, e);
    },
    [node, onMouseEnter]
  );

  const handleMouseLeave = useCallback(() => {
    setIsHovered(false);
    onMouseLeave?.(node);
  }, [node, onMouseLeave]);

  const handleFocus = useCallback(() => {
    setIsFocused(true);
  }, []);

  const handleBlur = useCallback(() => {
    setIsFocused(false);
  }, []);

  // Get tier colors
  const nodeColors = TIER_CONFIG[node.tier].colors;

  // Format balance
  const displayBalance = formatCurrency(node.totalBalance);

  // Calculate text positions
  const centerX = x + width / 2;
  const nameY = y + height * 0.28;
  const balanceY = y + height * 0.55;

  // Expand indicator position
  const expandX = x + width - 22;
  const expandY = y + 2;

  // Account count and type breakdown
  // Use accountCount from metadata when containedAccounts is empty (backend pre-lumped node)
  const accountCount = node.containedAccounts.length > 0
    ? node.containedAccounts.length
    : (node.accountCount ?? 0);
  const hasLiabilities = node.containedAccounts.some((a) =>
    isLiabilityAccount(a.accountType)
  );
  const hasAssets = node.containedAccounts.some(
    (a) => !isLiabilityAccount(a.accountType)
  );

  // Determine the type label
  let typeLabel = '';
  if (hasLiabilities && hasAssets) {
    typeLabel = ' (mixed)';
  } else if (hasLiabilities && !hasAssets) {
    typeLabel = ' (liabilities)';
  }

  // Visual state
  const showHighlight = isHovered || isFocused || isHighlighted;

  return (
    <g
      className="lumped-node"
      role="button"
      tabIndex={0}
      aria-label={`${node.name}: ${accountCount} accounts${typeLabel}, Total balance: ${displayBalance}. Click to expand.`}
      aria-expanded={isExpanded}
      onClick={handleClick}
      onKeyDown={handleKeyDown}
      onMouseEnter={handleMouseEnter}
      onMouseLeave={handleMouseLeave}
      onFocus={handleFocus}
      onBlur={handleBlur}
      style={{ cursor: onClick ? 'pointer' : 'default' }}
    >
      {/* Focus ring (rendered behind node) */}
      {isFocused && (
        <rect
          x={x - 3}
          y={y - 3}
          width={width + 6}
          height={height + 6}
          rx={8}
          ry={8}
          fill="none"
          stroke={A11Y.FOCUS_RING.split(' ')[2]}
          strokeWidth={2}
        />
      )}

      {/* Node rectangle with light background and border */}
      <rect
        x={x}
        y={y}
        width={width}
        height={height}
        rx={6}
        ry={6}
        fill={nodeColors.background}
        stroke={nodeColors.border}
        strokeWidth={NODE.BORDER_WIDTH}
        opacity={showHighlight ? 1 : 0.95}
        filter={showHighlight ? 'url(#node-shadow-hover)' : 'url(#node-shadow)'}
        className="transition-all duration-150 ease-in-out"
      />

      {/* Hover highlight effect */}
      {showHighlight && (
        <rect
          x={x}
          y={y}
          width={width}
          height={height}
          rx={6}
          ry={6}
          fill={nodeColors.border}
          opacity={0.08}
          className="pointer-events-none"
        />
      )}

      {/* Name label */}
      <text
        x={centerX}
        y={nameY}
        textAnchor="middle"
        fill={nodeColors.text}
        fontSize={11}
        fontWeight={showHighlight ? 600 : 500}
        className="sankey-label"
      >
        {node.name}
      </text>

      {/* Balance - displayed prominently */}
      <text
        x={centerX}
        y={balanceY}
        textAnchor="middle"
        fill={nodeColors.text}
        fontSize={14}
        fontWeight={600}
        className="sankey-balance"
      >
        {displayBalance}
      </text>

      {/* Expand indicator */}
      <g className="expand-icon" transform={`translate(${expandX}, ${expandY})`}>
        <rect
          x={0}
          y={0}
          width={20}
          height={16}
          rx={3}
          ry={3}
          fill={showHighlight ? nodeColors.border : 'transparent'}
          opacity={showHighlight ? 0.2 : 0}
          stroke={nodeColors.border}
          strokeWidth={1}
          className="transition-all duration-150"
        />
        <text
          x={10}
          y={12}
          textAnchor="middle"
          fill={nodeColors.text}
          fontSize={10}
          fontWeight={600}
        >
          {isExpanded ? '-' : '+'}
        </text>
      </g>

      {/* Account count indicator with type breakdown */}
      <text
        x={centerX}
        y={balanceY + 16}
        textAnchor="middle"
        fill={nodeColors.text}
        opacity={0.7}
        fontSize={9}
      >
        {accountCount} account{accountCount !== 1 ? 's' : ''}
        {typeLabel}
      </text>
    </g>
  );
}

export const LumpedNode = memo(LumpedNodeComponent);
export default LumpedNode;
